import { AnimatePresence, motion } from 'framer-motion';
import { AudioWaveform, FileEdit, FileSearch, type LucideIcon } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface CaseProcessingLoaderProps {
  title?: string;
  onComplete?: () => void;
}

interface ProcessingStep {
  id: string;
  label: string;
  duration: number;
  icon: LucideIcon;
  iconColor: string;
}

const steps: ProcessingStep[] = [
  {
    id: 'audio',
    label: 'Analyzing audio recording...',
    duration: 10000, // 10 seconds (0-30%)
    icon: AudioWaveform,
    iconColor: 'text-blue-500',
  },
  {
    id: 'extraction',
    label: 'Extracting entities...',
    duration: 12000, // 12 seconds (30-70%)
    icon: FileSearch,
    iconColor: 'text-purple-500',
  },
  {
    id: 'filling',
    label: 'Populating the case input...',
    duration: 8000, // 8 seconds (70-95%)
    icon: FileEdit,
    iconColor: 'text-green-500',
  },
];

const KnipperCaseDetailProcessingPageComponent: React.FC<CaseProcessingLoaderProps> = ({
  title,
  onComplete,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const animateProgress = useCallback(
    (start: number, end: number, duration: number): Promise<void> => {
      return new Promise(resolve => {
        // Cancel any existing animation
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        const startTime = Date.now();
        const update = () => {
          // Check if component is still mounted before updating state
          if (!isMountedRef.current) {
            animationFrameRef.current = null;
            resolve();
            return;
          }

          const now = Date.now();
          const elapsed = now - startTime;
          const progressRatio = Math.min(elapsed / duration, 1);
          const currentProgress = start + (end - start) * progressRatio;

          setProgress(currentProgress);

          // Force DOM update for progress bar to prevent visual lag
          if (progressBarRef.current) {
            progressBarRef.current.style.width = `${currentProgress}%`;
          }

          if (progressRatio < 1) {
            animationFrameRef.current = requestAnimationFrame(update);
          } else {
            animationFrameRef.current = null;
            resolve();
          }
        };
        animationFrameRef.current = requestAnimationFrame(update);
      });
    },
    [], // No dependencies needed - only uses refs and setState
  );

  useEffect(() => {
    isMountedRef.current = true;

    const runSteps = async () => {
      // Check if still mounted before each step
      if (!isMountedRef.current) return;

      // Step 1: Audio
      setCurrentStepIndex(0);
      await animateProgress(0, 30, steps[0].duration);

      if (!isMountedRef.current) return;

      // Step 2: Extraction
      setCurrentStepIndex(1);
      await animateProgress(30, 70, steps[1].duration);

      if (!isMountedRef.current) return;

      // Step 3: Filling
      setCurrentStepIndex(2);
      await animateProgress(70, 95, steps[2].duration);

      if (!isMountedRef.current) return;

      // Stay at 95% - don't go to 100%
      // onComplete will be called when backend updates the state
      onComplete?.();
    };

    runSteps();

    return () => {
      // Mark component as unmounted to prevent state updates
      isMountedRef.current = false;

      // Cleanup: cancel any pending animation frame
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [onComplete, animateProgress]);

  return (
    <div
      className="flex flex-col items-center justify-center h-full w-full bg-background"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Processing case details"
    >
      <div className="w-full max-w-md p-8 flex flex-col items-center">
        {/* Animated Icon Based on Current Step */}
        <div className="relative mb-8" aria-hidden="true">
          <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-20"></div>
          <div className="relative w-20 h-20 bg-card rounded-full shadow-xl flex items-center justify-center border border-border overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStepIndex}
                initial={{ scale: 0, rotate: -180, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                exit={{ scale: 0, rotate: 180, opacity: 0 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeInOut',
                }}
                className="flex items-center justify-center"
              >
                {React.createElement(steps[currentStepIndex].icon, {
                  className: `w-10 h-10 ${steps[currentStepIndex].iconColor}`,
                  strokeWidth: 2,
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Status Text */}
        <div className="h-16 flex items-center justify-center mb-6 w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStepIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <h2 className="text-xl font-semibold text-foreground" id="processing-status">
                {title || steps[currentStepIndex].label}
              </h2>
              {!title && (
                <p className="text-sm text-muted-foreground mt-1">
                  AI Agent is processing case details
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress Bar */}
        <div
          className="w-64 h-2 bg-muted rounded-full overflow-hidden relative"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-labelledby="processing-status"
        >
          <div
            ref={progressBarRef}
            className="absolute top-0 left-0 h-full bg-primary rounded-full"
            style={{
              width: `${progress}%`,
              transition: 'width 0.075s linear',
              willChange: 'width',
            }}
          />
        </div>

        {/* Percentage */}
        <div
          className="mt-2 text-xs font-medium text-muted-foreground font-mono"
          aria-hidden="true"
        >
          {Math.round(progress)}%
        </div>

        {/* Screen reader only announcement for progress updates */}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {steps[currentStepIndex].label} Progress: {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
};

export const KnipperCaseDetailProcessingPage = React.memo(KnipperCaseDetailProcessingPageComponent);
KnipperCaseDetailProcessingPage.displayName = 'KnipperCaseDetailProcessingPage';
